import { KeyRound } from 'lucide-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { useRoleOptions, useMailProviders } from '@/features/access/api'
import { MailboxPanel } from '@/features/access/components/MailboxPanel'
import { RoleChecklist } from '@/features/access/components/RoleChecklist'
import { MAIL_ACCOUNT_MANAGE_PERM } from '@/features/access/permissions'
import { useAuth } from '@/lib/auth/AuthProvider'
import { FormField } from '../components/FormField'
import type { PersonelFormMode, PersonelFormValues } from '../schemas/personelFormSchema'
import { FormSection, SwitchCard } from './FormBits'
import { FULL_ROW } from './formTabs'
import { useFieldError } from './useFieldError'

export function PanelFormTab({
  mode,
  canResetPassword,
  hadPanel,
}: {
  mode: PersonelFormMode
  canResetPassword: boolean
  hadPanel: boolean
}) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { register, control, setValue } = useFormContext<PersonelFormValues>()
  const err = useFieldError()
  const panelErisim = useWatch({ control, name: 'panel_erisim' })
  const sifreSifirla = useWatch({ control, name: 'sifre_sifirla' })
  const mailboxAssign = useWatch({ control, name: 'mailbox_assign' })
  const mailboxProviderId = useWatch({ control, name: 'mailbox_provider_id' }) ?? ''
  const mailboxSecret = useWatch({ control, name: 'mailbox_secret' }) ?? ''
  const mailboxSignature = useWatch({ control, name: 'mailbox_signature' }) ?? ''
  const editing = mode === 'edit'
  const granting = panelErisim && (!editing || !hadPanel)
  const canMailbox = can(MAIL_ACCOUNT_MANAGE_PERM)
  const roles = useRoleOptions().data ?? []
  const providers = useMailProviders(canMailbox && !!panelErisim).data ?? []

  return (
    <FormSection title={t('hr.personel.form.sections.panel')} icon={<KeyRound />} description={t('hr.personel.form.hints.panel')}>
      <div className={FULL_ROW}>
        <SwitchCard
          checked={!!panelErisim}
          onChange={(v) => {
            if (editing && hadPanel) return
            setValue('panel_erisim', v, { shouldValidate: true, shouldDirty: true })
          }}
          title={t('hr.personel.form.panelAccess')}
          hint={t('hr.personel.form.panelAccessHint')}
        />
      </div>

      {panelErisim ? (
        <>
          <FormField label={t('hr.personel.form.firmaEmail')} htmlFor="f-firma" required error={err('firma_email')} className="sm:col-span-2 lg:col-span-1">
            <Input id="f-firma" type="email" className="h-10 rounded-xl" {...register('firma_email')} />
          </FormField>

          {granting ? (
            <div className={FULL_ROW}>
              <FormField label={t('hr.personel.form.roles')} required error={err('role_ids')}>
                <Controller
                  control={control}
                  name="role_ids"
                  render={({ field }) => (
                    <RoleChecklist roles={roles} value={field.value ?? []} onChange={field.onChange} />
                  )}
                />
              </FormField>
            </div>
          ) : null}

          {canMailbox ? (
            <div className={FULL_ROW}>
              <MailboxPanel
                enabled={!!mailboxAssign}
                onEnabledChange={(v) => setValue('mailbox_assign', v, { shouldValidate: true, shouldDirty: true })}
                providers={providers}
                providerId={mailboxProviderId}
                onProviderId={(v) => setValue('mailbox_provider_id', v, { shouldValidate: true, shouldDirty: true })}
                secret={mailboxSecret}
                onSecret={(v) => setValue('mailbox_secret', v, { shouldValidate: true, shouldDirty: true })}
                signatureHtml={mailboxSignature}
                onSignatureHtml={(v) => setValue('mailbox_signature', v, { shouldDirty: true })}
                errors={{
                  providerId: err('mailbox_provider_id') || undefined,
                  secret: err('mailbox_secret') || undefined,
                }}
              />
            </div>
          ) : null}

          {editing && hadPanel && canResetPassword ? (
            <div className={FULL_ROW}>
              <SwitchCard
                checked={!!sifreSifirla}
                onChange={(v) => setValue('sifre_sifirla', v, { shouldValidate: true, shouldDirty: true })}
                title={t('hr.personel.form.resetPassword')}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </FormSection>
  )
}
